import { X } from 'lucide-react'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'

  // Header (optional)
  title?: string
  subtitle?: string
  icon?: React.ReactNode
  iconClassName?: string
  showCloseButton?: boolean

  // Body
  children: React.ReactNode

  // Footer (optional)
  footer?: React.ReactNode

  // Behavior
  closeOnBackdropClick?: boolean
  disabled?: boolean
  className?: string
  'data-testid'?: string
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-7xl'
}

export default function Modal({
  isOpen,
  onClose,
  size = 'md',
  title,
  subtitle,
  icon,
  iconClassName = 'bg-primary/10',
  showCloseButton = true,
  children,
  footer,
  closeOnBackdropClick = true,
  disabled = false,
  className = '',
  'data-testid': dataTestId
}: ModalProps) {
  const handleBackdropClick = () => {
    if (closeOnBackdropClick && !disabled) {
      onClose()
    }
  }

  const handleCloseClick = () => {
    if (!disabled) {
      onClose()
    }
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className={`modal-box ${sizeClasses[size]} max-h-[90vh] flex flex-col ${className}`} data-testid={dataTestId}>
        {/* Header */}
        {(title || icon || showCloseButton) && (
          <div className="flex items-center justify-between mb-6 flex-shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {icon && (
                <div className={`p-2 rounded-lg ${iconClassName}`}>
                  {icon}
                </div>
              )}
              {title && (
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-xl">{title}</h3>
                  {subtitle && <p className="text-sm text-base-content/70">{subtitle}</p>}
                </div>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={handleCloseClick}
                className="btn btn-sm btn-ghost btn-circle"
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`flex-1 overflow-y-auto ${footer ? 'mb-6' : ''}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex justify-end gap-2 pt-2 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>

      {/* Backdrop */}
      <div className="modal-backdrop" onClick={handleBackdropClick}></div>
    </dialog>
  )
}
